import React from 'react';
import '../../css/components/SkeletonSystem.css';

// ===== BASE SKELETON COMPONENT =====
export const BaseSkeleton = ({ 
  width = '100%', 
  height = '20px', 
  className = '', 
  rounded = true 
}) => {
  return (
    <div 
      className={`skeleton-base ${rounded ? 'rounded' : ''} ${className}`}
      style={{ width, height }}
      aria-label="Loading..."
    >
      <div className="skeleton-shimmer" />
    </div>
  );
};

// ===== TEXT SKELETON COMPONENT =====
export const TextSkeleton = ({ 
  lines = 1, 
  lastLineWidth = '60%', 
  lineHeight = '16px',
  className = '' 
}) => {
  return (
    <div className={`skeleton-text ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="skeleton-text-line"
          style={{
            height: lineHeight,
            width: index === lines - 1 ? lastLineWidth : '100%'
          }}
        />
      ))}
    </div>
  );
};

// ===== IMAGE SKELETON COMPONENT =====
export const ImageSkeleton = ({ 
  width = '100%', 
  height = '200px', 
  rounded = true,
  className = '' 
}) => {
  return (
    <div 
      className={`skeleton-image ${rounded ? 'rounded' : ''} ${className}`}
      style={{ width, height }}
      aria-label="Loading image..."
    />
  );
};

// ===== BUTTON SKELETON COMPONENT =====
export const ButtonSkeleton = ({ 
  size = 'medium', 
  width = 'auto',
  className = '' 
}) => {
  const sizeClass = `skeleton-button--${size}`;
  
  return (
    <div 
      className={`skeleton-button ${sizeClass} ${className}`}
      style={{ width }}
      aria-label="Loading button..."
    />
  );
};

// ===== CARD SKELETON COMPONENT =====
export const CardSkeleton = ({ 
  variant = 'event',
  showImage = true,
  showButton = true,
  className = '' 
}) => {
  return (
    <div className={`skeleton-card ${className}`} aria-label="Loading card...">
      {showImage && <div className="skeleton-card-image" />}
      
      <div className="skeleton-card-title" />
      
      <div className="skeleton-card-description">
        <div className="skeleton-card-description-line" />
        <div className="skeleton-card-description-line" />
        <div className="skeleton-card-description-line" />
      </div>
      
      {showButton && <div className="skeleton-card-button" />}
    </div>
  );
};

// ===== CAROUSEL SKELETON COMPONENT =====
export const CarouselSkeleton = ({ 
  items = 4, 
  itemsPerView = 4,
  showArrows = true,
  className = '' 
}) => {
  return (
    <div className={`skeleton-carousel ${className}`} aria-label="Loading carousel...">
      <div className="skeleton-carousel-container">
        {Array.from({ length: items }, (_, index) => (
          <div key={index} className="skeleton-carousel-item">
            <CardSkeleton variant="event" showImage={true} showButton={true} />
          </div>
        ))}
      </div>
      
      {showArrows && (
        <>
          <div className="skeleton-carousel-arrow skeleton-carousel-arrow--prev" />
          <div className="skeleton-carousel-arrow skeleton-carousel-arrow--next" />
        </>
      )}
    </div>
  );
};

// ===== GRID SKELETON COMPONENT =====
export const GridSkeleton = ({ 
  rows = 3, 
  cols = 4, 
  gap = '1rem',
  itemType = 'card', // 'card', 'text', 'image', 'button'
  className = '' 
}) => {
  const gridClass = `skeleton-grid skeleton-grid--${cols}cols`;
  const totalItems = rows * cols;
  
  const renderGridItem = () => {
    switch (itemType) {
      case 'card':
        return <CardSkeleton variant="event" showImage={true} showButton={true} />;
      case 'text':
        return <TextSkeleton lines={3} lastLineWidth="60%" />;
      case 'image':
        return <ImageSkeleton width="100%" height="200px" />;
      case 'button':
        return <ButtonSkeleton size="medium" width="100%" />;
      default:
        return <BaseSkeleton width="100%" height="100px" />;
    }
  };
  
  return (
    <div 
      className={`${gridClass} ${className}`}
      style={{ gap }}
      aria-label={`Loading grid with ${rows} rows and ${cols} columns...`}
    >
      {Array.from({ length: totalItems }, (_, index) => (
        <div key={index}>
          {renderGridItem()}
        </div>
      ))}
    </div>
  );
};

// ===== SPECIALIZED SKELETON COMPONENTS =====

// Event Card Skeleton (specific to your CurrentEventsSection)
export const EventCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`skeleton-card ${className}`} aria-label="Loading event card...">
      <div className="skeleton-card-image" />
      <div className="skeleton-card-title" />
      
      <div className="skeleton-card-description">
        <div className="skeleton-card-description-line" />
        <div className="skeleton-card-description-line" />
      </div>
      
      <div className="skeleton-card-button" />
    </div>
  );
};

// Student Type Card Skeleton (specific to your student types section)
export const StudentTypeCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`skeleton-card ${className}`} aria-label="Loading student type card...">
      <div className="skeleton-card-title" />
      
      <div className="skeleton-card-description">
        <div className="skeleton-card-description-line" />
        <div className="skeleton-card-description-line" />
        <div className="skeleton-card-description-line" />
      </div>
      
      <div className="skeleton-card-button" />
    </div>
  );
};

// Feature Card Skeleton (specific to your features section)
export const FeatureCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`skeleton-card ${className}`} aria-label="Loading feature card...">
      <div className="skeleton-card-title" />
      <div className="skeleton-card-description">
        <div className="skeleton-card-description-line" />
        <div className="skeleton-card-description-line" />
      </div>
    </div>
  );
};

// Quick Links Skeleton (specific to your quick links section)
export const QuickLinksSkeleton = ({ count = 5, className = '' }) => {
  return (
    <div className={`skeleton-quick-links ${className}`} aria-label="Loading quick links...">
      {Array.from({ length: count }, (_, index) => (
        <ButtonSkeleton key={index} size="medium" width="160px" />
      ))}
    </div>
  );
};

// Member Card Skeleton (generic for staff, HSB, etc.)
export const MemberCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`skeleton-member-card ${className}`} aria-label="Loading member card...">
      <div className="skeleton-member-headshot" />
      <div className="skeleton-member-content">
        <div className="skeleton-member-name" />
        <div className="skeleton-member-title" />
      </div>
    </div>
  );
};

// Staff Card Skeleton (alias for backwards compatibility)
export const StaffCardSkeleton = MemberCardSkeleton;

// Testimonial Card Skeleton (specific to your testimonial cards)
export const TestimonialCardSkeleton = ({ className = '' }) => {
  return (
    <div className={`skeleton-testimonial-card ${className}`} aria-label="Loading testimonial card...">
      <div className="skeleton-testimonial-image-section">
        <div className="skeleton-testimonial-headshot" />
      </div>
      <div className="skeleton-testimonial-content-section">
        <div className="skeleton-quote-icon" />
        <div className="skeleton-testimonial-text">
          <div className="skeleton-text-line" />
          <div className="skeleton-text-line" />
          <div className="skeleton-text-line" />
          <div className="skeleton-text-line" />
          <div className="skeleton-text-line" />
        </div>
        <div className="skeleton-testimonial-name" />
        <div className="skeleton-testimonial-grad-year" />
      </div>
    </div>
  );
};

// ===== DEFAULT EXPORT =====
const SkeletonSystem = {
  BaseSkeleton,
  TextSkeleton,
  ImageSkeleton,
  ButtonSkeleton,
  CardSkeleton,
  CarouselSkeleton,
  GridSkeleton,
  EventCardSkeleton,
  StudentTypeCardSkeleton,
  FeatureCardSkeleton,
  QuickLinksSkeleton,
  MemberCardSkeleton,
  StaffCardSkeleton,
  TestimonialCardSkeleton
};

export default SkeletonSystem;
